import React, { useEffect, useState, useContext } from 'react';
import { Elements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useHistory, useLocation } from 'react-router-dom';
import { createOrder } from '../../../api/orders';
import { CustomerContext } from '../../../contexts/CustomerContext';
import styles from './StripeTestCheckout.module.scss';

const stripePromise = loadStripe(process.env.STRIPE_PUBLISHABLE_KEY);

const CompleteInner = () => {
  const stripe = useStripe();
  const history = useHistory();
  const location = useLocation();
  const { customer, setCustomer } = useContext(CustomerContext);
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!stripe) {
      return;
    }

    const clientSecret = new URLSearchParams(location.search).get(
      'payment_intent_client_secret'
    );

    if (!clientSecret) {
      setMessage('No payment information found.');
      setIsLoading(false);
      setTimeout(() => history.push('/'), 3000);
      return;
    }

    stripe.retrievePaymentIntent(clientSecret).then(async ({ paymentIntent }) => {
      setIsLoading(false);

      switch (paymentIntent.status) {
        case 'succeeded':
          setMessage('Payment succeeded! Creating order...');

          // Get cart, email, and customer data from sessionStorage (stored before redirect)
          const cartId = sessionStorage.getItem('test_checkout_cart_id');
          const email = sessionStorage.getItem('test_checkout_email');
          const customerDataJson = sessionStorage.getItem('test_checkout_customer_data');

          if (cartId && email && customerDataJson) {
            try {
              const customerData = JSON.parse(customerDataJson);
              const { shippingAddress } = customerData;

              // Fetch payment intent with expanded payment method to get billing details
              const paymentIntentResponse = await fetch(`/api/v1/payment-intent/${paymentIntent.id}`);
              const paymentIntentData = await paymentIntentResponse.json();

              // Extract billing details from payment method
              const billingDetails = paymentIntentData.payment_method?.billing_details || {};
              const billingAddress = billingDetails.address || {};

              const orderData = {
                email: email,
                first_name: customerData.firstName,
                last_name: customerData.lastName,
                phone: customerData.phone,
                cart_id: cartId,
                payment_method: 'stripe_test',
                paypal_order: JSON.stringify({
                  paymentIntent: paymentIntent.id,
                  test_checkout: true
                }),
                paid_with_paypal: true,
                // Billing address from payment method
                address: billingAddress.line1 || '',
                address2: billingAddress.line2 || '',
                city: billingAddress.city || '',
                state: billingAddress.state || '',
                zip_code: billingAddress.postal_code || '',
                // Shipping address
                shipping_address: shippingAddress?.address1 || '',
                shipping_address2: shippingAddress?.address2 || '',
                shipping_city: shippingAddress?.city || '',
                shipping_state: shippingAddress?.state || '',
                shipping_zip: shippingAddress?.zip || '',
              };

              const response = await createOrder({ order: orderData });

              // Clear session storage
              sessionStorage.removeItem('test_checkout_cart_id');
              sessionStorage.removeItem('test_checkout_email');
              sessionStorage.removeItem('test_checkout_customer_data');

              // Clear cart from customer context
              setCustomer({ ...customer, cart: null });

              // Redirect to order complete page
              history.push(`/order/complete/${response.data.order.id}/${response.data.order.email}`);
            } catch (error) {
              console.error('Order creation error:', error);
              setMessage('Payment succeeded but order creation failed. Please contact support.');
              setTimeout(() => history.push('/'), 3000);
            }
          } else {
            setMessage('Payment succeeded! Redirecting...');
            setTimeout(() => history.push('/'), 2000);
          }
          break;
        case 'processing':
          setMessage('Your payment is processing. Redirecting...');
          setTimeout(() => history.push('/'), 3000);
          break;
        case 'requires_payment_method':
          setMessage('Your payment was not successful, please try again.');
          setTimeout(() => history.push('/test-checkout'), 3000);
          break;
        default:
          setMessage('Something went wrong. Redirecting...');
          setTimeout(() => history.push('/'), 3000);
          break;
      }
    }).catch((error) => {
      setIsLoading(false);
      setMessage('Error verifying payment. Redirecting...');
      setTimeout(() => history.push('/'), 3000);
    });
  }, [stripe, location, history]);

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <h1 className={styles.title}>Processing Payment</h1>

        {isLoading ? (
          <div className={styles.loadingState}>
            <div className={styles.spinner}></div>
            <p>Verifying your payment...</p>
          </div>
        ) : (
          <div className={message.includes('succeeded') ? styles.successMessage : styles.errorMessage}>
            {message}
          </div>
        )}
      </div>
    </div>
  );
};

const Complete = () => {
  return (
    <Elements stripe={stripePromise}>
      <CompleteInner />
    </Elements>
  );
};

export default Complete;
